import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Link2, Mail, Copy, Check, Send, Share2, Eye, X, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SharePlanDialog({ open, onOpenChange, plan, onShared }) {
  const [shareType, setShareType] = useState("link");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [includeProgress, setIncludeProgress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    if (shareType === "email" && !email.trim()) {
      toast.error("Please enter a recipient email");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/training-plans/${plan.id}/share`,
        {
          plan_id: plan.id,
          share_type: shareType,
          recipient_email: shareType === "email" ? email.trim() : null,
          message: message.trim() || null,
          include_progress: includeProgress,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShareResult(res.data);
      toast.success(shareType === "email" ? "Plan shared via email!" : "Share link created!");
      if (onShared) onShared();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to share plan");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (!shareResult) return;
    const fullUrl = `${window.location.origin}${shareResult.share_url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = (val) => {
    if (!val) {
      setShareResult(null);
      setEmail("");
      setMessage("");
      setIncludeProgress(false);
      setCopied(false);
      setShareType("link");
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="share-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-800">
            <Share2 className="w-5 h-5" /> Share Training Plan
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            {plan?.title}
          </DialogDescription>
        </DialogHeader>

        {!shareResult ? (
          <div className="space-y-4">
            {/* Share type toggle */}
            <div className="flex gap-2">
              <button
                data-testid="share-type-link"
                onClick={() => setShareType("link")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  shareType === "link"
                    ? "bg-emerald-100 text-emerald-800 ring-2 ring-emerald-500"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Link2 className="w-4 h-4" /> Share Link
              </button>
              <button
                data-testid="share-type-email"
                onClick={() => setShareType("email")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  shareType === "email"
                    ? "bg-emerald-100 text-emerald-800 ring-2 ring-emerald-500"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Mail className="w-4 h-4" /> By Email
              </button>
            </div>

            {/* Email input */}
            {shareType === "email" && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  data-testid="share-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="coach@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                />
              </div>
            )}

            {/* Optional message */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Message (optional)
              </label>
              <textarea
                data-testid="share-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Check out my training plan..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
              />
            </div>

            {/* Include progress toggle */}
            <label className="flex items-center gap-3 cursor-pointer" data-testid="include-progress-toggle">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={includeProgress}
                  onChange={(e) => setIncludeProgress(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-9 h-5 rounded-full transition-colors ${includeProgress ? "bg-emerald-500" : "bg-gray-300"}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform mt-0.5 ${includeProgress ? "translate-x-4.5 ml-[18px]" : "ml-0.5"}`} />
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Include my progress</span>
                <p className="text-[10px] text-gray-400">Share completion % and day-by-day progress</p>
              </div>
            </label>

            <Button
              data-testid="share-confirm-btn"
              onClick={handleShare}
              disabled={loading}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-sm font-semibold"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Sharing...</>
              ) : shareType === "email" ? (
                <><Send className="w-4 h-4 mr-1" /> Share via Email</>
              ) : (
                <><Link2 className="w-4 h-4 mr-1" /> Create Share Link</>
              )}
            </Button>
          </div>
        ) : (
          /* Success state */
          <div className="space-y-4" data-testid="share-success">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-emerald-800 mb-1">Plan Shared!</p>
              <p className="text-xs text-emerald-600">
                {shareResult.share_type === "email"
                  ? "A notification has been sent to the recipient"
                  : "Anyone with the link can view this plan"}
              </p>
            </div>

            {/* Share link display */}
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-xs text-gray-700 truncate font-mono">
                {window.location.origin}{shareResult.share_url}
              </div>
              <Button
                data-testid="copy-share-link-btn"
                onClick={copyLink}
                size="sm"
                variant="outline"
                className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="view-shared-plan-btn"
                onClick={() => window.open(shareResult.share_url, "_blank")}
                variant="outline"
                className="flex-1 text-xs"
              >
                <Eye className="w-3.5 h-3.5 mr-1" /> Preview
              </Button>
              <Button
                onClick={() => handleClose(false)}
                className="flex-1 text-xs bg-gray-800 hover:bg-gray-900"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
